import { Inject, Injectable } from '@angular/core';

import { IBetslipMessages } from '../../../../base/models/betslip-messages';
import {
    BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
    IBetslipMessagesProviderService,
} from '../../../../base/services/betslip-messages-provider.service';
import { SystemBankerSinglesSlipType } from '../../../bet-generation/slip-type';
import { ISystemSlipTypeInfo, SystemKey, SystemModes } from '../../models';
import { ISystemBetPickState } from '../../state';
import { SYSTEMS } from '../system-bet.constants';
import SystemFactoryService from '../system-factory.service';

interface IPickSummary {
    pickCount: number;
    bankerCount: number;
    unavailableCount: number;
}

export interface IOrderedSystemType {
    typeInfo: ISystemSlipTypeInfo;
    order: number;
}

@Injectable()
export default class SystemBetTypesCalculatorService {
    private systemModes: SystemModes;
    private messages: IBetslipMessages;

    constructor(
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messageProvider: IBetslipMessagesProviderService,
        private systemFactoryService: SystemFactoryService,
    ) {
        this.messages = messageProvider.getMessages();

        this.systemModes = this.messages.systemModes;
    }

    calculateAvailableTypes(picks: { [pickId: string]: ISystemBetPickState & { isOpen: boolean } }, isLinear: boolean): IOrderedSystemType[] {
        const pickSummary = this.getPickSummary(picks, isLinear);

        return this.getSystemTypes(pickSummary);
    }

    getTypeName(pickCount: number, bankerCount: number, slipTypeKey: string): string {
        if (bankerCount > 0) {
            const singleBanker = new SystemBankerSinglesSlipType();

            return this.getSystemBetName(singleBanker.key.toLowerCase(), pickCount, bankerCount);
        }

        return this.getSystemBetName(slipTypeKey.toLowerCase(), pickCount, bankerCount);
    }

    private getPickSummary(picks: { [pickId: string]: ISystemBetPickState & { isOpen: boolean } }, isLinear: boolean): IPickSummary {
        return Object.values(picks).reduce(
            (acc, pick) => {
                if (!pick.isSelected || (isLinear && pick.isLocked)) {
                    return acc;
                }

                if (!pick.isOpen && !isLinear) {
                    acc.unavailableCount++;
                }

                if (pick.isBanker) {
                    acc.bankerCount++;
                } else {
                    acc.pickCount++;
                }

                return acc;
            },
            { pickCount: 0, bankerCount: 0, unavailableCount: 0 },
        );
    }

    private getSystemTypes({ pickCount, bankerCount, unavailableCount }: IPickSummary): IOrderedSystemType[] {
        if (unavailableCount > 0 || pickCount + bankerCount < 3) {
            return [];
        }

        const systemTypes: IOrderedSystemType[] = [];

        if (bankerCount > 0) {
            const singleBanker = new SystemBankerSinglesSlipType();
            const typeName = this.getSystemBetName(singleBanker.key.toLowerCase(), pickCount, bankerCount);

            systemTypes.push({ typeInfo: { slipType: singleBanker, betCount: pickCount, typeName }, order: -1 });
        }

        const systemFactoryTypes = this.systemFactoryService.getSystemsByPicksCount(pickCount).map((slipType) => {
            const index = SYSTEMS.findIndex((system) => system.id === slipType.key.toLowerCase());

            const systemTypeConstant = SYSTEMS[index];
            const typeName = this.getSystemBetName(slipType.key.toLowerCase(), pickCount, bankerCount);

            return {
                typeInfo: {
                    slipType,
                    betCount: systemTypeConstant.bets,
                    typeName,
                },
                order: index,
            };
        });

        return [...systemTypes, ...systemFactoryTypes];
    }

    private getSystemBetName(slipKey: string, picksCount: number, bankerCount: number): string {
        if (!slipKey) {
            return '';
        }

        const systemBetName = this.systemModes[slipKey as keyof SystemModes];

        if (bankerCount > 0) {
            if (slipKey === SystemKey.BankerSingles) {
                return bankerCount + ' ' + this.messages.common.Bankers + ' + ' + picksCount + ' ' + this.messages.common.Picks;
            } else {
                return bankerCount + ' ' + this.messages.common.Bankers + ' - ' + systemBetName;
            }
        }

        return systemBetName;
    }
}
